import {
  DAY_OF_WEEK_VALUES,
  MERCHANT_DEFAULT_INVITATION_MESSAGE_MAX_LENGTH,
  MERCHANT_SAMPLE_COST_MAX
} from 'constants/settings';
import { roundMoney } from 'features/settings/utils/moneyUtils';
import type { DayOfWeek } from 'model/types/settings';
import { getSettingsValueAtPath } from 'utils/merchantSettingsObjRead';

// ==============================|| MERCHANT SETTINGS ||============================== //

/** Maps each logical setting key to its path under merchant `settingsObj`. */
const SETTING_PATHS = {
  dm_auto_reply: ['tiktok_shop', 'messaging', 'dm_auto_reply'],
  collect_spark_codes: ['tiktok_shop', 'messaging', 'collect_spark_codes'],
  content_reminder: ['tiktok_shop', 'messaging', 'content_dm_reminders', 'active'],
  content_dm_reminders_active_open_campaigns: ['tiktok_shop', 'messaging', 'content_dm_reminders', 'active_open_campaigns'],
  spark_code_reminder: ['tiktok_shop', 'messaging', 'spark_code_request_dm', 'active'],
  spark_code_request_dm_active_open_campaigns: ['tiktok_shop', 'messaging', 'spark_code_request_dm', 'active_open_campaigns'],
  sample_cost: ['analytics', 'sample_cost'],
  first_day_of_week: ['analytics', 'first_day_of_week'],
  default_invitation_message: ['tiktok_shop', 'campaign_config', 'default_invitation_message']
} as const satisfies Readonly<Record<string, readonly string[]>>;

export type MerchantSettingKey = keyof typeof SETTING_PATHS;

function toDayOfWeek(value: unknown): DayOfWeek | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim().toLowerCase();
  if ((DAY_OF_WEEK_VALUES as readonly string[]).includes(normalized)) {
    return normalized as DayOfWeek;
  }
  return undefined;
}

export class MerchantSettings {
  static readonly PATHS = SETTING_PATHS;

  private readonly settingsObj: unknown;

  constructor(settingsObj: unknown) {
    this.settingsObj = settingsObj;
  }

  get paths(): Record<MerchantSettingKey, readonly string[]> {
    return MerchantSettings.PATHS;
  }

  /** Reads `tiktok_shop.campaign_config.default_invitation_message`. */
  get invitationMessage(): string {
    const raw = getSettingsValueAtPath(this.settingsObj, MerchantSettings.PATHS.default_invitation_message);
    if (typeof raw !== 'string') {
      return '';
    }
    return raw.slice(0, MERCHANT_DEFAULT_INVITATION_MESSAGE_MAX_LENGTH);
  }

  /** Reads `analytics.first_day_of_week`. */
  get firstDayOfWeek(): DayOfWeek | undefined {
    const raw = getSettingsValueAtPath(this.settingsObj, MerchantSettings.PATHS.first_day_of_week);
    return toDayOfWeek(raw);
  }

  /** Reads `analytics.sample_cost`. */
  get sampleCost(): number | null {
    const raw = getSettingsValueAtPath(this.settingsObj, MerchantSettings.PATHS.sample_cost);
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    let n: number;
    if (typeof raw === 'number') {
      n = raw;
    } else if (typeof raw === 'string') {
      n = Number.parseFloat(raw.replaceAll(',', '.'));
    } else {
      return null;
    }
    if (Number.isNaN(n) || !Number.isFinite(n) || n < 0 || n > MERCHANT_SAMPLE_COST_MAX) {
      return null;
    }
    return roundMoney(n);
  }

  /** Coerces a nested settings value to boolean (for toggle fields). */
  getSettingsBooleanAtPath(path: readonly string[]): boolean {
    const raw = getSettingsValueAtPath(this.settingsObj, path);
    if (typeof raw === 'boolean') {
      return raw;
    }
    if (raw === 'true' || raw === 1) {
      return true;
    }
    return false;
  }

  /** Builds a nested patch object for `merchantUpdate` by looking up the path for the given setting key. */
  static buildPatch(key: MerchantSettingKey, value: unknown): Record<string, unknown> {
    const path = MerchantSettings.PATHS[key];
    let result: unknown = value;
    for (let i = path.length - 1; i >= 0; i--) {
      result = { [path[i]]: result };
    }
    return result as Record<string, unknown>;
  }
}
